import globals from "@homer.core/globals";
import application from "@homer.core/application";
import scheduler from "@homer.core/scheduler";
import queue from "@homer.core/queue.raf";
import whenImages from "@homer.core/when.images";
import isFalsy from "@homer.utility/validator.isfalsy";
import isString from "@homer.utility/validator.isstring";
import { modules, dependencies } from "../json/minimal.json";
import { rewrite } from "../../../../../package.json";

const matchViewModule = /\/view(?:\/|$)/;
const localeMarker = "/locale/";
const localePlaceholder = "${locale}";
const debugModulePrefix = "@debug/";
const head = document.head;
const cache = {};
const moduleIndex = new Map(modules.map((moduleName, index) => [moduleName, index]));
const viewModules = Object.freeze(modules.filter((moduleName) => matchViewModule.test(moduleName)));
const dependencyIndexCache = new Map();
const dependencyNameCache = new Map();
const localeSuffixes = application.locales.map((locale) => `/${locale}`);

// Concrete configured locales share one inventory row while retaining their requested URL.
const getInventoryName = (name) => {
    if (moduleIndex.has(name) || !name.includes(localeMarker)) {
        return name;
    }

    const suffix = localeSuffixes.find((localeSuffix) => name.endsWith(localeSuffix));

    return suffix ? `${name.slice(0, -suffix.length)}/${localePlaceholder}` : name;
};

const getRecursiveDependencies = (deps = []) => {
    if (!deps.length) {
        return [];
    }

    const results = [];

    deps.forEach((index) => {
        results.push(index, ...collectDependencies(index));
    });

    return results;
};

const collectDependencies = (index) => {
    if (dependencyIndexCache.has(index)) {
        return dependencyIndexCache.get(index);
    }

    const direct = dependencies[index] || [];

    if (!direct.length) {
        dependencyIndexCache.set(index, direct);

        return direct;
    }

    const resolved = [];

    direct.forEach((child) => {
        resolved.push(child, ...collectDependencies(child));
    });

    dependencyIndexCache.set(index, resolved);

    return resolved;
};

const getDependencyNames = (index) => {
    if (dependencyNameCache.has(index)) {
        return dependencyNameCache.get(index);
    }

    const names = [...new Set(getRecursiveDependencies(dependencies[index] || []))].map((dependencyIndex) => {
        return modules[dependencyIndex];
    });

    dependencyNameCache.set(index, names);

    return names;
};

class Inventory {
    #queue = new Set();
    #storage;

    constructor() {
        this.#storage = new Set([...Object.keys(globals?.modules || {}), ...(globals?.demand?.list || [])]);

        globals.demand.on("dependency", (event, module) => {
            this.add(module.name);
        });
    }

    add(name) {
        this.#queue.delete(name);
        this.#storage.add(name);
    }

    has(name) {
        return this.#storage.has(name);
    }

    queue(name) {
        this.#queue.add(name);
    }

    queued(name) {
        return this.#queue.has(name);
    }
}

class Module {
    // The browser registry consumes canonical view IDs without triggering their loading.
    get views() {
        return viewModules;
    }

    #inventory;

    load(name) {
        const inventoryName = getInventoryName(rewrite[name] || name);

        // Debug tools intentionally bypass the inventory while other free component strings remain inert.
        if (
            !moduleIndex.has(inventoryName) &&
            (typeof inventoryName !== "string" || !inventoryName.startsWith(debugModulePrefix))
        ) {
            return Promise.reject(new Error(`module '${name}' is not part of the public inventory`));
        }

        const resolved = this.resolve(name);

        return globals.demand.request(resolved.name).then(([module]) => module);
    }

    resolve(name) {
        if (isFalsy(name) || !isString(name)) {
            throw new TypeError("argument `name` must be of type `string` (non-empty)");
        }

        name = rewrite[name] || name;

        const nameExpanded = name.replace(localePlaceholder, application.locale);

        if (cache[nameExpanded]) {
            return cache[nameExpanded];
        }

        // Debug modules trade inventory dependency graphs for direct, on-demand resolution.
        if (name.startsWith(debugModulePrefix)) {
            const version = application.version;

            return (cache[nameExpanded] = Object.freeze({
                name: nameExpanded,
                version,
                url: URL.resolve(`/module/${nameExpanded}?module.version=${version}`).href,
                dependencies: [],
            }));
        }

        const inventoryName = getInventoryName(name);
        let index = moduleIndex.get(inventoryName);
        const exact = index !== undefined;

        // package exports, e.g. "./locale/de-CH" or "./style", do not
        // have a version entry but do inherit their parent package version
        // so we sanitize dependency names here
        const namePackage = inventoryName
            .split("/")
            .slice(0, inventoryName.startsWith("@") ? 2 : 1)
            .join("/");

        index ??= moduleIndex.get(namePackage);

        if (index === undefined) {
            return;
        }

        const version = application.version;
        const url = URL.resolve(`/module/${nameExpanded}?module.version=${version}`).href;

        return (cache[nameExpanded] = Object.freeze({
            name: nameExpanded,
            version,
            url,
            dependencies: exact || inventoryName === namePackage ? getDependencyNames(index) : [],
        }));
    }

    preload(name, importance = "low") {
        name = (this.resolve(name) || {}).name;

        if (!name) {
            return;
        }

        const inventory = this.#inventory || (this.#inventory = new Inventory());

        if (inventory.queued(name) || inventory.has(name)) {
            return;
        }

        inventory.queue(name);

        void (scheduler.yield[importance === "critical" ? "high" : importance] || scheduler.yield.low)(whenImages).then(() => {
            if (inventory.has(name)) {
                return;
            }

            return globals.demand.request(name);
        });
    }

    process(dependency) {
        return new Promise((resolve, reject) => {
            const { name, type } = dependency;

            switch (type) {
                case "application/javascript":
                    const script = document.createElement("script");

                    script.async = true;
                    script.text = dependency.source;
                    script.setAttribute("demand-id", name);
                    script.nonce = application.nonce; // CSP-MARK

                    // Defer non-critical inline execution to a low scheduler lane.
                    scheduler.yield
                        .low()
                        .then(() => {
                            head.appendChild(script);

                            resolve(dependency.source);
                        })
                        .catch(reject);

                    break;
                case "text/css":
                    const style = document.createElement("style");

                    style.textContent = dependency.source;
                    style.setAttribute("demand-id", name);
                    style.setAttribute("nonce", application.nonce);
                    style.nonce = application.nonce; // CSP-MARK

                    // Resolve after the style node was appended by the RAF queue.
                    queue.add(() => {
                        try {
                            head.appendChild(style);

                            void globals.demand.provide(name, () => style);

                            resolve(dependency.source);
                        } catch (error) {
                            reject(error);
                        }
                    });

                    break;
                default:
                    resolve(dependency.source);

                    break;
            }
        });
    }
}

export default new Module();
